import React from 'react';
import {
  Box,
  Flex,
  VStack,
  Input,
  Button,
  FieldRoot,
  FieldErrorText,
  PopoverRoot,
  PopoverTrigger,
  PopoverPositioner,
  PopoverContent,
  PopoverBody,
} from '@chakra-ui/react';
import { CHAT_PROFANITY_ERROR } from '@/utils/chatProfanity';

type Props = {
  newMessage: string;
  /** Receives the new text and where the caret ended up, which the `@` picker needs. */
  setNewMessage: (v: string, caret: number) => void;
  onSubmit: (e: React.FormEvent) => void;
  sending: boolean;
  error: string | null;
  uploadingImage: boolean;
  uploadingPdf: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  documentInputRef: React.RefObject<HTMLInputElement | null>;
  onImageSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDocumentSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** The `@` picker. Owned by ChatThread; this component only gives it an anchor. */
  messageInputRef?: React.RefObject<HTMLInputElement | null>;
  onMessageKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onCaretChange?: (position: number) => void;
  mentionSuggestions?: React.ReactNode;
};

export function ChatInput({
  newMessage,
  setNewMessage,
  onSubmit,
  sending,
  error,
  uploadingImage,
  uploadingPdf,
  fileInputRef,
  documentInputRef,
  onImageSelect,
  onDocumentSelect,
  messageInputRef,
  onMessageKeyDown,
  onCaretChange,
  mentionSuggestions,
}: Props) {
  const hasProfanityError = error === CHAT_PROFANITY_ERROR;

  return (
    <Box flexShrink={0} px={4} pt={4} pb="calc(1rem + env(safe-area-inset-bottom, 0px))" bg="white" borderTopWidth={1} borderColor="gray.200">
      <input
        ref={fileInputRef as React.RefObject<HTMLInputElement>}
        id="event-chat-image"
        name="event-chat-image"
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        style={{ display: 'none' }}
        onChange={onImageSelect}
      />
      <input
        ref={documentInputRef as React.RefObject<HTMLInputElement>}
        id="event-chat-document"
        name="event-chat-document"
        type="file"
        accept="application/pdf,.pdf"
        style={{ display: 'none' }}
        onChange={onDocumentSelect}
      />
      <form onSubmit={onSubmit} autoComplete="off">
        {/* position:relative so the mention popover can anchor above this row. */}
        <Flex align="center" gap={3} position="relative">
          {mentionSuggestions}
          <PopoverRoot>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                colorPalette="blue"
                fontSize="3xl"
                lineHeight="none"
                p={0}
                minW="auto"
                h="auto"
                aria-label="Add attachment"
                loading={uploadingImage || uploadingPdf}
                disabled={uploadingImage || uploadingPdf}
              >
                +
              </Button>
            </PopoverTrigger>
            <PopoverPositioner>
              <PopoverContent>
                <PopoverBody py={2} px={2}>
                  <VStack align="stretch" gap={1}>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      justifyContent="flex-start"
                      onClick={() => {
                        fileInputRef.current?.click();
                      }}
                    >
                      Add Photo
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      justifyContent="flex-start"
                      disabled={uploadingPdf}
                      onClick={() => {
                        if (!uploadingPdf) {
                          documentInputRef.current?.click();
                        }
                      }}
                    >
                      Add Document
                    </Button>
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            </PopoverPositioner>
          </PopoverRoot>
          <Input
            ref={messageInputRef as React.RefObject<HTMLInputElement>}
            id="event-chat-message"
            name="event-chat-compose"
            type="text"
            inputMode="text"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            enterKeyHint="send"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value, e.target.selectionStart ?? e.target.value.length)}
            onKeyDown={onMessageKeyDown}
            onSelect={(e) => onCaretChange?.(e.currentTarget.selectionStart ?? 0)}
            onClick={(e) => onCaretChange?.(e.currentTarget.selectionStart ?? 0)}
            onKeyUp={(e) => onCaretChange?.(e.currentTarget.selectionStart ?? 0)}
            placeholder="Type a message..."
            flex={1}
            minW={0}
            size="md"
            borderRadius="full"
            borderColor="gray.300"
            bg="gray.50"
            _focus={{ borderColor: 'blue.500', bg: 'white', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
            _placeholder={{ color: 'gray.400' }}
            paddingLeft="10px"
          />
          <Button
            type="submit"
            disabled={sending || !newMessage.trim() || hasProfanityError}
            variant="ghost"
            colorPalette="blue"
            fontWeight="bold"
            fontSize="md"
            flexShrink={0}
            _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
          >
            Send
          </Button>
        </Flex>
      </form>
      {error && (
        <FieldRoot invalid={!!error}>
          <FieldErrorText mt={2}>{error}</FieldErrorText>
        </FieldRoot>
      )}
    </Box>
  );
}
